if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';
        this.classList.remove('quick-add-modal--product-preview');

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);
            this.classList.add('quick-add-modal--product-preview');
            this.initializePreviewLayout();
            this.initializePreviewGallery();
            this.initializePreviewScroll();

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      initializePreviewLayout() {
        const infoContainer = this.querySelector('.product__info-container');
        const priceBlock = this.querySelector('[id^="price-"]');

        if (!infoContainer) return;

        this.querySelectorAll(
          '.product__text, .product__description, share-button, .product__tax, .installment, .product__inventory, .product__sku, .product-popup-modal__opener, .complementary-products'
        ).forEach((element) => element.remove());

        this.ensurePreviewRating(infoContainer, priceBlock);
        this.ensurePreviewBundles();
        this.ensurePreviewAccordions(infoContainer);
        this.orderPreviewContent(infoContainer);
      }

      initializePreviewScroll() {
        const infoWrapper = this.querySelector('.product__info-wrapper');
        if (!infoWrapper || infoWrapper.dataset.previewScrollReady === 'true') return;

        infoWrapper.dataset.previewScrollReady = 'true';
        infoWrapper.addEventListener(
          'wheel',
          (event) => {
            if (window.matchMedia('(max-width: 749px)').matches) return;
            const maxScrollTop = infoWrapper.scrollHeight - infoWrapper.clientHeight;
            if (maxScrollTop <= 0) return;

            const nextScrollTop = Math.min(maxScrollTop, Math.max(0, infoWrapper.scrollTop + event.deltaY));
            if (nextScrollTop === infoWrapper.scrollTop) return;

            event.preventDefault();
            infoWrapper.scrollTop = nextScrollTop;
          },
          { passive: false }
        );
      }

      ensurePreviewRating(infoContainer, priceBlock) {
        const judgeMeRating = this.querySelector('.jdgm-preview-badge');
        const rating = judgeMeRating || this.querySelector('.rating-wrapper');
        if (!rating) return;

        if (judgeMeRating) {
          this.querySelectorAll('.rating-wrapper').forEach((nativeRating) => nativeRating.remove());
        }

        rating.classList.add('quick-add-preview__rating');

        if (priceBlock) {
          infoContainer.insertBefore(rating, priceBlock);
        } else {
          const title = this.querySelector('.product__title');
          title?.insertAdjacentElement('afterend', rating);
        }
      }

      ensurePreviewBundles() {
        const bundle = this.querySelector('.quantity-discount-selector');
        if (!bundle) return;

        bundle.classList.add('quick-add-preview__bundle');
        if (typeof window.initializeQuantityDiscountSelector === 'function') {
          window.initializeQuantityDiscountSelector(this.modalContent);
        }
      }

      ensurePreviewAccordions(infoContainer) {
        const sourceAccordions = Array.from(infoContainer.querySelectorAll('.product__accordion'));
        if (!sourceAccordions.length) return;

        const accordions = document.createElement('div');
        accordions.className = 'quick-add-preview__accordions';
        const accordionDetails = sourceAccordions.map((accordion) => accordion.querySelector('details')).filter(Boolean);

        sourceAccordions.forEach((accordion) => {
          accordion.classList.remove('quick-add-hidden');
          accordion.classList.add('quick-add-preview__accordion');
          const details = accordion.querySelector('details');
          details?.removeAttribute('open');
          details?.addEventListener('toggle', () => {
            if (!details.open) return;
            accordionDetails.forEach((otherDetails) => {
              if (otherDetails !== details) otherDetails.removeAttribute('open');
            });
          });
          accordions.appendChild(accordion);
        });
        infoContainer.appendChild(accordions);
      }

      orderPreviewContent(infoContainer) {
        const orderedElements = [
          this.querySelector('.product__title'),
          this.querySelector('.quick-add-preview__rating'),
          this.querySelector('[id^="price-"]'),
          this.querySelector('variant-selects, variant-radios'),
          this.querySelector('.quick-add-preview__bundle'),
          this.querySelector('product-form.product-form')?.parentElement,
          this.querySelector('.quick-add-preview__accordions'),
        ].filter(Boolean);

        orderedElements.forEach((element) => {
          if (element !== infoContainer) infoContainer.appendChild(element);
        });

        Array.from(infoContainer.children).forEach((element) => {
          if (!orderedElements.includes(element)) element.remove();
        });
      }

      initializePreviewGallery() {
        const mediaGallery = this.querySelector('media-gallery');
        const mediaItems = Array.from(this.querySelectorAll('.product__media-item'));
        const thumbnailItems = Array.from(this.querySelectorAll('.thumbnail-list__item'));
        const thumbnailList = this.querySelector('.thumbnail-list');
        const currentCounter = this.querySelector('.slider-counter--current');
        const totalCounter = this.querySelector('.slider-counter--total');
        const galleryViewer = this.querySelector('[id^="GalleryViewer-"]');

        if (!mediaItems.length) return;

        this.querySelectorAll('.slider-button--prev, .slider-button--next').forEach((button) => button.remove());

        const setActiveMedia = (index) => {
          const boundedIndex = (index + mediaItems.length) % mediaItems.length;
          const activeMedia = mediaItems[boundedIndex];
          const activeMediaId = activeMedia?.dataset.mediaId;

          mediaItems.forEach((item, itemIndex) => {
            const isActive = itemIndex === boundedIndex;
            item.classList.toggle('is-active', isActive);
            item.toggleAttribute('hidden', !isActive);
          });

          if (activeMediaId && typeof mediaGallery?.setActiveMedia === 'function') {
            mediaGallery.skipNextMediaScroll = true;
            mediaGallery.setActiveMedia(activeMediaId, false);
          }

          let activeThumbnailItem = null;
          thumbnailItems.forEach((item) => {
            const thumbnail = item.querySelector('.thumbnail');
            if (!thumbnail) return;
            const isActive = item.dataset.target === activeMediaId;
            if (isActive) {
              thumbnail.setAttribute('aria-current', 'true');
              activeThumbnailItem = item;
            } else {
              thumbnail.removeAttribute('aria-current');
            }
          });

          if (currentCounter) currentCounter.textContent = boundedIndex + 1;

          if (activeThumbnailItem && thumbnailList) {
            const targetLeft = Math.max(
              0,
              activeThumbnailItem.offsetLeft - (thumbnailList.clientWidth - activeThumbnailItem.offsetWidth) / 2
            );
            thumbnailList.scrollTo({ left: targetLeft, behavior: 'smooth' });
          }
        };

        if (totalCounter) totalCounter.textContent = mediaItems.length;
        const initialIndex = mediaItems.findIndex((item) => item.classList.contains('is-active'));
        setActiveMedia(initialIndex >= 0 ? initialIndex : 0);

        if (galleryViewer && !galleryViewer.querySelector('.quick-add-preview__media-arrows')) {
          const arrows = document.createElement('div');
          arrows.className = 'quick-add-preview__media-arrows';
          arrows.innerHTML = `
            <button class="quick-add-preview__media-arrow quick-add-preview__media-arrow--prev" type="button" aria-label="Previous media"></button>
            <button class="quick-add-preview__media-arrow quick-add-preview__media-arrow--next" type="button" aria-label="Next media"></button>
          `;
          galleryViewer.appendChild(arrows);

          arrows.querySelector('.quick-add-preview__media-arrow--prev')?.addEventListener('click', () => {
            const activeIndex = mediaItems.findIndex((item) => item.classList.contains('is-active'));
            setActiveMedia(activeIndex - 1);
          });

          arrows.querySelector('.quick-add-preview__media-arrow--next')?.addEventListener('click', () => {
            const activeIndex = mediaItems.findIndex((item) => item.classList.contains('is-active'));
            setActiveMedia(activeIndex + 1);
          });
        }

        thumbnailItems.forEach((item) => {
          item.querySelector('.thumbnail')?.addEventListener(
            'click',
            (event) => {
              event.preventDefault();
              event.stopPropagation();
              event.stopImmediatePropagation();
              const mediaIndex = mediaItems.findIndex(
                (mediaItem) => mediaItem.dataset.mediaId === item.dataset.target
              );
              if (mediaIndex >= 0) setActiveMedia(mediaIndex);
            },
            { capture: true }
          );
        });

      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        productElement.querySelectorAll('.product-design-breadcrumb, .product__view-details').forEach((element) =>
          element.remove()
        );

        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}
